import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Inbox,
  AlertTriangle,
  Clock,
  Trophy,
  TrendingUp,
  IndianRupee,
} from "lucide-react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Cell,
  PieChart,
  Pie,
} from "recharts";
import api from "@/lib/api";
import { STAGE_META, SOURCE_META, stageLabel } from "@/lib/funnel";

const KPI = ({ icon: Icon, label, value, tone, testid, onClick }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`text-left bg-white border border-slate-200 rounded-lg p-5 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all ${onClick ? "cursor-pointer" : "cursor-default"}`}
  >
    <div className="flex items-center justify-between">
      <span className="text-xs uppercase tracking-wider font-semibold text-slate-500">{label}</span>
      <span className={`h-8 w-8 rounded-md flex items-center justify-center ${tone}`}>
        <Icon className="h-4 w-4" />
      </span>
    </div>
    <p className="mt-3 text-3xl font-bold tracking-tighter text-slate-900 font-display">{value}</p>
  </button>
);

const SOURCE_COLORS = ["#0ea5e9", "#f97316", "#22c55e", "#8b5cf6", "#64748b"];

export default function Dashboard() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  if (!data)
    return <div className="h-64 flex items-center justify-center text-slate-400">Loading dashboard…</div>;

  const stageData = Object.entries(data.by_stage)
    .filter(([k]) => !["won", "lost"].includes(k))
    .filter(([k]) => STAGE_META[k])
    .map(([k, v]) => ({ name: stageLabel(k), value: v, cat: STAGE_META[k]?.cat || "pending" }));

  const sourceData = Object.entries(data.by_source).map(([k, v]) => ({
    name: SOURCE_META[k]?.label || k || "Other",
    value: v,
  }));

  const catColor = { pending: "#f59e0b", in_transit: "#3b82f6", won: "#22c55e", lost: "#94a3b8" };

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <h1 className="font-display text-2xl font-semibold text-slate-900">Dashboard</h1>
        <p className="text-slate-500 text-sm">Pipeline health & action overview</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        <KPI icon={Inbox} label="Total Leads" value={data.total} tone="bg-slate-100 text-slate-700" onClick={() => navigate("/leads")} testid="kpi-total" />
        <KPI icon={AlertTriangle} label="Needs Action" value={data.needs_action} tone="bg-amber-100 text-amber-700" onClick={() => navigate("/needs-action")} testid="kpi-needs-action" />
        <KPI icon={Clock} label="Overdue" value={data.overdue} tone="bg-red-100 text-red-700" onClick={() => navigate("/needs-action?view=overdue")} testid="kpi-overdue" />
        <KPI icon={Trophy} label="Won" value={data.won} tone="bg-green-100 text-green-700" testid="kpi-won" />
        <KPI icon={TrendingUp} label="Conversion" value={`${data.conversion_rate}%`} tone="bg-blue-100 text-blue-700" testid="kpi-conversion" />
        <KPI icon={IndianRupee} label="Pipeline ₹" value={`${(data.pipeline_value / 100000).toFixed(1)}L`} tone="bg-violet-100 text-violet-700" testid="kpi-pipeline" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
          <h3 className="font-display text-lg font-medium text-slate-900 mb-4">Active Pipeline by Stage</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={stageData} margin={{ left: -20 }}>
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#64748b" }} interval={0} angle={-25} textAnchor="end" height={70} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748b" }} />
              <Tooltip cursor={{ fill: "#f1f5f9" }} />
              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                {stageData.map((d, i) => (
                  <Cell key={i} fill={catColor[d.cat]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
          <h3 className="font-display text-lg font-medium text-slate-900 mb-4">Leads by Source</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={sourceData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} innerRadius={45}>
                {sourceData.map((d, i) => (
                  <Cell key={i} fill={SOURCE_COLORS[i % SOURCE_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-3 space-y-1.5">
            {sourceData.map((d, i) => (
              <div key={d.name} className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2 text-slate-600">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ background: SOURCE_COLORS[i % SOURCE_COLORS.length] }} />
                  {d.name}
                </span>
                <span className="font-semibold text-slate-900">{d.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
